import { maskPromptPayId } from '@pos/promptpay';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import {
  listBranches,
  listBranchManualPaymentChannels,
  listBranchPaymentSettings,
  listManualPaymentChannels,
} from '../../data/fetchers';
import {
  saveBranchPaymentSettings,
  upsertBranchManualPaymentChannel,
  upsertManualPaymentChannel,
} from '../../data/mutators';
import { canEditMasterData, visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type {
  Branch,
  BranchManualPaymentChannel,
  BranchPaymentSettings,
  ManualPaymentChannel,
} from '../../types/domain';
import { ManualChannelDialog, PromptPayDialog } from './PaymentMethodDialogs';
import {
  branchScopeLabel,
  type BranchSelection,
  channelBranchIds,
  planChannelAssignments,
  planPromptPayRemoval,
  planPromptPaySave,
  type PromptPayAccountInput,
  type PromptPayAccountRow,
  promptPayAccountRows,
} from './paymentSettingsModel';
import { SettingsHeader } from './SettingsHeader';

const EMPTY_BRANCHES: Branch[] = [];
const EMPTY_SETTINGS: BranchPaymentSettings[] = [];
const EMPTY_CHANNELS: ManualPaymentChannel[] = [];
const EMPTY_ASSIGNMENTS: BranchManualPaymentChannel[] = [];

const HEADER_TITLE = 'วิธีชำระเงิน';
const HEADER_SUBTITLE =
  'เงินสดและ QR นิติ-SCB เปิดทุกสาขาอัตโนมัติ วิธีอื่นเพิ่มเองและเลือกสาขาได้';

const BUILT_IN_METHODS = [
  { name: 'เงินสด', detail: 'รับเงินหน้าร้าน' },
  { name: 'QR นิติ-SCB', detail: 'ลูกค้าสแกนจ่าย ระบบยืนยันยอดให้อัตโนมัติ' },
];

type PromptPayDialogState = { row: PromptPayAccountRow | null };
// The id is fixed when the dialog opens so a retry never creates a duplicate.
type ChannelDialogState = {
  channel: ManualPaymentChannel | null;
  newChannelId: string;
};
type PendingRemoval =
  | { kind: 'promptpay'; row: PromptPayAccountRow }
  | { kind: 'channel'; channel: ManualPaymentChannel };

function MethodRow({
  title,
  detail,
  scope,
  actions,
}: {
  title: ReactNode;
  detail?: ReactNode;
  scope: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-surface-1 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-semibold text-ink-1">{title}</p>
        {detail ? <p className="text-sm-label text-ink-3">{detail}</p> : null}
        <p className="mt-1 text-sm-label text-ink-2">ใช้ที่: {scope}</p>
      </div>
      {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
    </div>
  );
}

const removalCopy = (pending: PendingRemoval) =>
  pending.kind === 'promptpay'
    ? {
        title: `ลบบัญชีพร้อมเพย์ ${pending.row.account.accountName} ?`,
        description:
          'สาขาที่ใช้บัญชีนี้จะไม่แสดงพร้อมเพย์ที่ POS ส่วนรายการขายเดิมยังอยู่ครบ',
        confirm: 'ยืนยันลบบัญชี',
      }
    : {
        title: `เลิกใช้ ${pending.channel.name} ?`,
        description:
          'วิธีนี้จะหายจาก POS ทุกสาขา ส่วนรายการขายเดิมยังอยู่ครบ และเปิดใช้อีกครั้งได้',
        confirm: 'ยืนยันเลิกใช้',
      };

export function PaymentMethodsView() {
  const queryClient = useQueryClient();
  const sessionUser = useSession((state) => state.user);
  const canEdit = sessionUser ? canEditMasterData(sessionUser) : false;
  const isOwner = sessionUser?.role === 'owner';

  const { data: allBranches = EMPTY_BRANCHES } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const settingsQuery = useQuery({
    queryKey: ['branch-payment-settings'],
    queryFn: listBranchPaymentSettings,
  });
  const channelsQuery = useQuery({
    queryKey: ['manual-payment-channels'],
    queryFn: listManualPaymentChannels,
  });
  const assignmentsQuery = useQuery({
    queryKey: ['branch-manual-payment-channels'],
    queryFn: listBranchManualPaymentChannels,
  });
  const settings = settingsQuery.data ?? EMPTY_SETTINGS;
  const channels = channelsQuery.data ?? EMPTY_CHANNELS;
  const assignments = assignmentsQuery.data ?? EMPTY_ASSIGNMENTS;
  const isReady =
    settingsQuery.isSuccess &&
    channelsQuery.isSuccess &&
    assignmentsQuery.isSuccess;
  const loadFailed =
    settingsQuery.isError || channelsQuery.isError || assignmentsQuery.isError;

  const visibleIds = sessionUser
    ? visibleBranchIds(
        sessionUser,
        allBranches.map((branch) => branch.id),
      )
    : [];
  const branches = allBranches.filter((branch) =>
    visibleIds.includes(branch.id),
  );

  const [promptPayDialog, setPromptPayDialog] =
    useState<PromptPayDialogState | null>(null);
  const [channelDialog, setChannelDialog] = useState<ChannelDialogState | null>(
    null,
  );
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(
    null,
  );

  const refresh = () => {
    for (const queryKey of [
      ['branch-payment-settings'],
      ['manual-payment-channels'],
      ['branch-manual-payment-channels'],
    ]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  const settingsMutation = useMutation({
    mutationFn: (records: BranchPaymentSettings[]) =>
      Promise.all(records.map(saveBranchPaymentSettings)),
    onSuccess: () => {
      setPromptPayDialog(null);
      setPendingRemoval(null);
    },
    onSettled: refresh,
  });

  const channelMutation = useMutation({
    mutationFn: async (input: {
      channel: ManualPaymentChannel;
      writes: BranchManualPaymentChannel[];
      writeCatalog: boolean;
    }) => {
      // Assignments are rejected until their catalog entry is active.
      if (input.writeCatalog) await upsertManualPaymentChannel(input.channel);
      await Promise.all(input.writes.map(upsertBranchManualPaymentChannel));
    },
    onSuccess: () => {
      setChannelDialog(null);
      setPendingRemoval(null);
    },
    onSettled: refresh,
  });

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <SettingsHeader
          title={HEADER_TITLE}
          subtitle={HEADER_SUBTITLE}
          status={{ label: 'จำกัดสิทธิ์ผู้จัดการขึ้นไป', tone: 'neutral' }}
        />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะผู้จัดการขึ้นไป"
            sub="ให้ผู้จัดการหรือเจ้าของร้านตั้งค่าวิธีชำระเงิน"
          />
        </div>
      </div>
    );
  }

  const promptPayRows = promptPayAccountRows(branches, settings);
  const activeChannels = channels.filter((channel) => channel.active);
  const archivedChannels = channels.filter((channel) => !channel.active);

  const savePromptPay = (
    account: PromptPayAccountInput,
    selection: BranchSelection,
  ) => {
    const records = planPromptPaySave({
      account,
      selection,
      previous: promptPayDialog?.row ?? null,
      branches,
      settings,
    });
    if (records.length > 0) settingsMutation.mutate(records);
    else setPromptPayDialog(null);
  };

  const saveChannel = (name: string, branchIds: string[]) => {
    if (!channelDialog) return;
    const existing = channelDialog.channel;
    const channel = {
      id: existing?.id ?? channelDialog.newChannelId,
      name,
      active: true,
    };
    channelMutation.mutate({
      channel,
      writeCatalog: !existing || existing.name !== name,
      writes: planChannelAssignments({
        channelId: channel.id,
        name,
        selectedBranchIds: branchIds,
        branches,
        assignments,
        nextSortOrder: activeChannels.length + 1,
      }),
    });
  };

  const confirmRemoval = () => {
    if (pendingRemoval?.kind === 'promptpay') {
      settingsMutation.mutate(
        planPromptPayRemoval(pendingRemoval.row, settings),
      );
    } else if (pendingRemoval?.kind === 'channel') {
      channelMutation.mutate({
        channel: { ...pendingRemoval.channel, active: false },
        writes: [],
        writeCatalog: true,
      });
    }
  };

  const removalPending =
    settingsMutation.isPending || channelMutation.isPending;
  const removal = pendingRemoval ? removalCopy(pendingRemoval) : null;
  const openPromptPayDialog = (row: PromptPayAccountRow | null) => {
    settingsMutation.reset();
    setPromptPayDialog({ row });
  };
  const openChannelDialog = (channel: ManualPaymentChannel | null) => {
    channelMutation.reset();
    setChannelDialog({
      channel,
      newChannelId: `manual-${crypto.randomUUID()}`,
    });
  };
  const openRemoval = (pending: PendingRemoval) => {
    settingsMutation.reset();
    channelMutation.reset();
    setPendingRemoval(pending);
  };

  return (
    <div className="bo-fade">
      <SettingsHeader
        title={HEADER_TITLE}
        subtitle={HEADER_SUBTITLE}
        status={{
          label: isReady
            ? `${BUILT_IN_METHODS.length + promptPayRows.length + activeChannels.length} วิธีที่เปิดอยู่`
            : 'กำลังโหลดข้อมูล',
          tone: isReady ? 'ok' : 'neutral',
        }}
      />
      <div className="space-y-6">
        {loadFailed ? (
          <p role="alert" className="rounded-card bg-warning-50 p-3 text-ink-2">
            โหลดการตั้งค่าไม่สำเร็จ กรุณารีเฟรชหน้านี้ก่อนแก้ไข
          </p>
        ) : null}

        <Card title="เปิดให้ทุกสาขาอัตโนมัติ">
          <div
            data-testid="built-in-payment-methods"
            className="grid gap-3 md:grid-cols-2"
          >
            {BUILT_IN_METHODS.map((method) => (
              <MethodRow
                key={method.name}
                title={method.name}
                detail={method.detail}
                scope="ทุกสาขา"
              />
            ))}
          </div>
        </Card>

        <Card
          title="พร้อมเพย์ส่วนบุคคล"
          actions={
            <Btn
              kind="primary"
              disabled={!isReady}
              onClick={() => openPromptPayDialog(null)}
            >
              เพิ่มบัญชีพร้อมเพย์
            </Btn>
          }
        >
          {promptPayRows.length === 0 ? (
            <p className="text-sm-label text-ink-3">
              ยังไม่ได้เพิ่มบัญชี POS จะไม่แสดงพร้อมเพย์จนกว่าจะเพิ่มบัญชีที่นี่
            </p>
          ) : (
            <div data-testid="promptpay-accounts" className="space-y-3">
              {promptPayRows.map((row) => {
                const locked = row.source === 'store' && !isOwner;
                return (
                  <MethodRow
                    key={row.key}
                    title={row.account.accountName}
                    detail={maskPromptPayId(row.account.promptpayId)}
                    scope={branchScopeLabel(row.branchIds, branches)}
                    actions={
                      locked ? undefined : (
                        <>
                          <Btn
                            aria-label={`แก้ไข ${row.account.accountName}`}
                            onClick={() => openPromptPayDialog(row)}
                          >
                            แก้ไข
                          </Btn>
                          <Btn
                            kind="ghost"
                            aria-label={`ลบ ${row.account.accountName}`}
                            onClick={() =>
                              openRemoval({ kind: 'promptpay', row })
                            }
                          >
                            ลบ
                          </Btn>
                        </>
                      )
                    }
                  />
                );
              })}
            </div>
          )}
        </Card>

        <Card
          title="วิธีชำระเงินอื่น"
          actions={
            <Btn
              kind="primary"
              disabled={!isReady}
              onClick={() => openChannelDialog(null)}
            >
              เพิ่มวิธีชำระเงิน
            </Btn>
          }
        >
          {activeChannels.length === 0 ? (
            <p className="text-sm-label text-ink-3">
              เช่น บัตรสวัสดิการแห่งรัฐ หรือโครงการที่พนักงานตรวจการรับเงินเอง
            </p>
          ) : (
            <div data-testid="manual-channels" className="space-y-3">
              {activeChannels.map((channel) => (
                <MethodRow
                  key={channel.id}
                  title={channel.name}
                  scope={branchScopeLabel(
                    channelBranchIds(channel.id, branches, assignments),
                    branches,
                  )}
                  actions={
                    <>
                      <Btn
                        aria-label={`แก้ไข ${channel.name}`}
                        onClick={() => openChannelDialog(channel)}
                      >
                        แก้ไข
                      </Btn>
                      {isOwner ? (
                        <Btn
                          kind="ghost"
                          aria-label={`เลิกใช้ ${channel.name}`}
                          onClick={() =>
                            openRemoval({ kind: 'channel', channel })
                          }
                        >
                          เลิกใช้
                        </Btn>
                      ) : null}
                    </>
                  }
                />
              ))}
            </div>
          )}
          {archivedChannels.length > 0 ? (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm-label text-ink-2">
                เลิกใช้แล้ว ({archivedChannels.length})
              </summary>
              <div className="mt-3 space-y-2">
                {archivedChannels.map((channel) => (
                  <div
                    key={channel.id}
                    className="flex items-center justify-between gap-3 rounded-card bg-surface-2 p-2"
                  >
                    <span className="text-ink-3">{channel.name}</span>
                    {isOwner ? (
                      <Btn
                        kind="ghost"
                        disabled={channelMutation.isPending}
                        onClick={() =>
                          channelMutation.mutate({
                            channel: { ...channel, active: true },
                            writes: [],
                            writeCatalog: true,
                          })
                        }
                      >
                        เปิดใช้อีกครั้ง
                      </Btn>
                    ) : null}
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </Card>
      </div>

      {promptPayDialog ? (
        <PromptPayDialog
          row={promptPayDialog.row}
          branches={branches}
          canManageAllBranches={isOwner}
          hasOtherAccounts={promptPayRows.some(
            (row) => row.key !== promptPayDialog.row?.key,
          )}
          saving={settingsMutation.isPending}
          failed={settingsMutation.isError}
          onClose={() => setPromptPayDialog(null)}
          onSave={savePromptPay}
        />
      ) : null}
      {channelDialog ? (
        <ManualChannelDialog
          initialName={channelDialog.channel?.name ?? ''}
          initialBranchIds={
            channelDialog.channel
              ? channelBranchIds(
                  channelDialog.channel.id,
                  branches,
                  assignments,
                )
              : []
          }
          isEdit={Boolean(channelDialog.channel)}
          branches={branches}
          canManageAllBranches={isOwner}
          saving={channelMutation.isPending}
          failed={channelMutation.isError}
          onClose={() => setChannelDialog(null)}
          onSave={saveChannel}
        />
      ) : null}
      <Modal
        open={Boolean(pendingRemoval)}
        title={removal?.title}
        description={removal?.description}
        tone="danger"
        onClose={() => {
          if (!removalPending) setPendingRemoval(null);
        }}
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Btn
              className="w-full sm:w-auto"
              disabled={removalPending}
              onClick={() => setPendingRemoval(null)}
            >
              ยกเลิก
            </Btn>
            <Btn
              kind="danger"
              className="w-full sm:w-auto"
              disabled={removalPending}
              onClick={confirmRemoval}
            >
              {removalPending ? 'กำลังบันทึก…' : removal?.confirm}
            </Btn>
          </div>
        }
      >
        {settingsMutation.isError || channelMutation.isError ? (
          <p role="alert" className="text-danger">
            บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
